<template>
  <b-form @submit.stop.prevent="handleSubmit">
    <b-row>
      <b-col>
        <b-alert
          :show="hasBaseServerErrors()"
          variant="danger"
          dismissible
        >
          <p
            class="mb-0"
            v-for="(error, index) in baseServerErrors"
            :key="index"
          >
            {{ error }}
          </p>
        </b-alert>
      </b-col>
    </b-row>

    <b-row>
      <b-col lg="5">
        <b-form-group
          :label="fieldLabels.name"
          label-for="client-name"
        >
          <b-form-input
            id="client-name"
            v-model="$v.form.name.$model"
            :state="$v.form.name.$dirty ? !$v.form.name.$error : null"
            type="text"
          />
          <b-form-invalid-feedback
            v-if="$v.form.name.$dirty"
            id="client-name-feedback"
          >
            <span v-if="!$v.form.name.required">Please enter a name.</span>
            <span v-if="!$v.form.name.maxLength">
              The name must be less than {{ $v.form.name.$params.maxLength.max + 1 }} characters.
            </span>
            <span v-if="!$v.form.name.serverFailed">{{ serverErrors.name }}</span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>

      <b-col lg="3">
        <b-form-group
          :label="fieldLabels.code"
          label-for="client-code"
        >
          <b-form-input
            id="client-code"
            v-model="$v.form.code.$model"
            :state="$v.form.code.$dirty ? !$v.form.code.$error : null"
            type="text"
            placeholder="ABCD"
          />
          <b-form-invalid-feedback
            v-if="$v.form.code.$dirty"
            id="client-code-feedback"
          >
            <span v-if="!$v.form.code.required">Please enter a code.</span>
            <span v-if="!$v.form.code.maxLength">
              The code must be less than {{ $v.form.code.$params.maxLength.max + 1 }} characters.
            </span>
            <span v-if="!$v.form.code.serverFailed">{{ serverErrors.code }}</span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>

      <b-col lg="4">
        <b-form-group
          :label="fieldLabels.customer_identifier"
          label-for="client-customer_identifier"
        >
          <b-form-input
            id="client-customer_identifier"
            v-model="$v.form.customer_identifier.$model"
            :state="$v.form.customer_identifier.$dirty ? !$v.form.customer_identifier.$error : null"
            type="text"
          />
          <b-form-invalid-feedback
            v-if="$v.form.customer_identifier.$dirty"
            id="client-customer_identifier-feedback"
          >
            <!-- prettier-ignore -->
            <span v-if="!$v.form.customer_identifier.maxLength">
              The customer identifier must be less than {{ $v.form.customer_identifier.$params.maxLength.max + 1 }} characters.
            </span>
            <span v-if="!$v.form.customer_identifier.serverFailed">{{ serverErrors.customer_identifier }}</span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>

      <b-col lg="6">
        <b-form-group
          :label="fieldLabels.organization_id"
          label-for="client-organization_id"
        >
          <organization-select
            id="client-organization_id"
            :select-class="{ 'is-invalid': $v.form.organization_id.$dirty && $v.form.organization_id.$error }"
            :value="form.organization"
            @input="onOrganizationChange"
          />
          <b-form-invalid-feedback
            v-if="$v.form.organization_id.$dirty"
            id="client-organization_id-feedback"
          >
            <span v-if="!$v.form.organization_id.required">Please enter an organization.</span>
            <span v-if="!$v.form.organization_id.serverFailed">
              {{ serverErrors.organization_id }}
            </span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>

      <b-col
        lg="6"
      >
        <b-form-group
          :label="fieldLabels.invoice_format_id"
          label-for="client-invoice_format_id"
        >
          <invoice-formats-select
            id="client-invoice_format_id"
            :select-class="{ 'is-invalid': $v.form.invoice_format_id.$dirty && $v.form.invoice_format_id.$error }"
            :value="form.invoice_format"
            @input="onInvoiceFormatChange"
          />
          <b-form-invalid-feedback
            v-if="$v.form.invoice_format_id.$dirty"
            id="client-invoice_format_id-feedback"
          >
            <span v-if="!$v.form.invoice_format_id.required">Please enter an invoice format.</span>
            <span v-if="!$v.form.invoice_format_id.serverFailed">{{ serverErrors.invoice_format_id }}</span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>

      <b-col>
        <b-form-group
          :label="fieldLabels.manager_ids"
          label-for="client-manager_ids"
        >
          <employee-select
            id="client-manager_ids"
            :select-class="{ 'is-invalid': $v.form.manager_ids.$dirty && $v.form.manager_ids.$error }"
            :value="form.managers"
            @input="onManagersChange"
            placeholder="Managers"
            multiple
          />
          <b-form-invalid-feedback
            v-if="$v.form.manager_ids.$dirty"
            id="client-manager_ids-feedback"
          >
            <span v-if="!$v.form.manager_ids.serverFailed">
              {{ serverErrors.manager_ids }}
            </span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>



      <b-col
        v-if="newForm"
        lg="6"
      >
        <b-form-group
          :label="fieldLabels.billing_email"
          label-for="client-billing_email"
        >
          <b-form-input
            id="client-billing_email"
            v-model="$v.form.billing_email.$model"
            autocomplete="new-password"
            :state="$v.form.billing_email.$dirty ? !$v.form.billing_email.$error : null"
            type="text"
            placeholder="example@email.com"
          />
          <b-form-invalid-feedback
            v-if="$v.form.billing_email.$dirty"
            id="client-billing_email-feedback"
          >
            <span v-if="!$v.form.billing_email.required">Please enter a billing email.</span>
            <span v-if="!$v.form.billing_email.email">Please enter a valid email.</span>
            <span v-if="!$v.form.billing_email.maxLength">
              The billing email must be less than {{ $v.form.billing_email.$params.maxLength.max + 1 }} characters.
            </span>
            <span v-if="!$v.form.billing_email.serverFailed">{{ serverErrors.billing_email }}</span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>

      <b-col
        v-if="newForm"
        cols="12"
      >
        <b-form-group
          label=""
          label-for="client-create_location"
        >
          <b-form-checkbox
            id="client-create_location"
            v-model="$v.form.create_location.$model"
          >
            Create Location from Client details
          </b-form-checkbox>
        </b-form-group>
      </b-col>
    </b-row>

    <hr class="mt-4">

    <b-row>
      <b-col
        offset-lg="6"
        lg="3"
        order="2"
        order-lg="1"
      >
        <b-button
          variant="link-dark"
          block
          id="client-close-button"
          @click="$emit('on-cancel')"
        >
          Cancel
        </b-button>
      </b-col>
      <b-col
        lg="3"
        order="1"
        order-lg="2"
      >
        <b-button
          id="client-submit-button"
          block
          type="submit"
          variant="primary"
          :disabled="processing"
        >
          Save
        </b-button>
      </b-col>
    </b-row>
  </b-form>
</template>

<script>
import { required, requiredIf, maxLength, email } from 'vuelidate/lib/validators';
import formMixin from '@/mixins/formMixin';
import OrganizationSelect from '@/components/admin/organizations/Select.vue';
import EmployeeSelect from '@/components/admin/users/EmployeeSelect.vue';
import InvoiceFormatsSelect from '@/components/admin/invoiceFormats/Select.vue';

export default {
  name: 'ClientsForm',
  components: {
    OrganizationSelect,
    EmployeeSelect,
    InvoiceFormatsSelect,
  },
  mixins: [formMixin],
  props: {
    id: [String, Number],
    newForm: Boolean,
    organization: Object,
    organization_id: [String, Number],
    code: String,
    name: String,
    customer_identifier: String,
    manager_ids: Array,
    managers: Array,
    invoice_format_id: [String, Number],
    invoice_format: Object,
    create_location: Boolean,
    billing_email: String,
  },
  data() {
    return {
      form: {
        organization_id: this.organization_id,
        organization: this.organization,
        code: this.code,
        name: this.name,
        customer_identifier: this.customer_identifier,
        manager_ids: this.manager_ids,
        managers: this.managers,
        invoice_format_id: this.invoice_format_id,
        invoice_format: this.invoice_format,
        create_location: this.create_location,
        ...(this.newForm ? { billing_email: this.billing_email || '' } : {}),
      },
      fieldLabels: {
        organization_id: 'Organization *',
        code: 'Code *',
        name: 'Name *',
        customer_identifier: 'Customer ID',
        manager_ids: 'Managers',
        invoice_format_id: 'Invoice Format *',
        billing_email: 'Billing Email *',
      },
      invoiceFormatOptions: [],
    };
  },
  validations: {
    form: {
      organization_id: {
        required,
        serverFailed() {
          return !this.hasServerErrors('organization_id');
        },
      },
      code: {
        required,
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('code');
        },
      },
      create_location: {},
      name: {
        required,
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('name');
        },
      },
      customer_identifier: {
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('customer_identifier');
        },
      },
      manager_ids: {
        serverFailed() {
          return !this.hasServerErrors('manager_ids');
        },
      },
      invoice_format_id: {
        required,
        serverFailed() {
          return !this.hasServerErrors('invoice_format_id');
        },
      },
      billing_email: {
        required: requiredIf(function () {
          return this.newForm;
        }),
        email,
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('billing_email');
        },
      },
    },
  },
  methods: {
    onOrganizationChange(organization) {
      this.form.organization_id = organization ? organization.id : '';
      this.form.organization = organization;
    },
    onManagersChange(managers) {
      this.form.managers = managers;
      this.form.manager_ids = managers.map((manager) => manager.id);
    },
    onInvoiceFormatChange(invoiceFormat) {
      this.form.invoice_format_id = invoiceFormat ? invoiceFormat.id : '';
      this.form.invoice_format = invoiceFormat;
    },
  },
};
</script>
